<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const textbookAlignedPoints = [
  "Poem subah se shaam tak ek green par life ka scene dikhati hai jahan children play karte hain aur old people unhein dekh kar apna past yaad karte hain.",
  "William Blake childhood innocence, natural joy aur human life cycle ko ek simple but symbolic scene me combine karte hain.",
  "Exam prep me central idea, tone, symbolism aur darkening green ki significance sab se important hote hain.",
];

const chapterCoverage = [
  "Morning to evening movement",
  "Children and old people",
  "Central idea",
  "Echoing and darkening green",
  "Tone and symbolism",
  "Life-cycle interpretation",
];

const quickRevisionPoints = [
  "Poem ek din ke scene ko life-cycle se connect karti hai.",
  "Children innocence aur energy ko represent karte hain.",
  "Old people memory aur later life stage ko show karte hain.",
  "Echoing green joy ka symbol hai.",
  "Darkening green passage of time aur rest ka symbol hai.",
];

const readingQuestions = [
  {
    question: "What happens in the poem The Echoing Green?",
    answer: "Poem me subah ka cheerful scene dikhaya gaya hai jahan birds gaati hain, bells ring karti hain aur children green par khelte hain. Old folk, specially Old John, un ko dekh kar apna bachpan yaad karte hain. Shaam hote hote children thak jate hain aur green darkening ho jati hai.",
  },
  {
    question: "What is the central idea of the poem?",
    answer: "Central idea joy of childhood aur life ke natural cycle ko highlight karna hai. Poem dikhati hai ke youth, old age aur final rest life ke connected stages hain.",
  },
  {
    question: "What do the children and old people represent in the poem?",
    answer: "Children innocence, energy aur beginning of life ko represent karte hain, jab ke old people experience, memory aur later stage of life ko. Dono mil kar human life ka complete cycle dikhate hain.",
  },
  {
    question: "Why is the green called echoing in the beginning and darkening in the end?",
    answer: "Beginning me green laughter, play aur sound se full hoti hai is liye usay echoing kaha gaya hai. End me din khatam hota hai, light aur energy dono kam ho jati hain, is liye green darkening ban jati hai.",
  },
  {
    question: "How does the poem connect nature with human life?",
    answer: "Poem ek din ki movement ko human life ke stages se compare karti hai. Morning youth ko, noon active life ko aur evening rest aur old age ko suggest karti hai.",
  },
];

const literaryPoints = [
  {
    label: "Tone",
    detail: "Tone cheerful, innocent aur nostalgic hai. End me slight sadness aur peaceful acceptance aa jati hai, lekin overall poem gentle hi rehti hai.",
  },
  {
    label: "Imagery",
    detail: "Sun, merry bells, skylark, thrush, children, oak tree aur mothers' laps jesi images poem ko vivid aur warm banati hain.",
  },
  {
    label: "Symbolism",
    detail: "Green innocence aur life ka symbol hai. Echoing green active joyful life ko, jab ke darkening green passage of time aur life's decline ko symbolize karti hai.",
  },
  {
    label: "Message",
    detail: "Poem ka message yeh hai ke life temporary hai magar beautiful bhi hai. Childhood ki happiness aur old age ki reflection dono insani journey ka hissa hain.",
  },
];

const mcqs = [
  {
    question: "Why is the green called echoing at the beginning of the poem?",
    options: [
      { label: "a", text: "because it is empty and silent", verdict: "incorrect", reason: "Beginning me poem silence nahi, lively sound aur movement show karti hai." },
      { label: "b", text: "because the place is full of laughter and happy sounds", verdict: "correct", reason: "Children ki playfulness aur natural sounds ki wajah se green `echoing` lagti hai." },
      { label: "c", text: "because old people are crying there", verdict: "incorrect", reason: "Old people nostalgic hain, lekin crying central image nahi." },
      { label: "d", text: "because the weather is stormy", verdict: "incorrect", reason: "Poem cheerful morning scene present karti hai, stormy weather nahi." },
    ],
  },
  {
    question: "The darkening green mainly symbolizes:",
    options: [
      { label: "a", text: "the destruction of nature", verdict: "incorrect", reason: "Poem ka focus environmental destruction nahi, passage of time hai." },
      { label: "b", text: "the end of childhood joy forever", verdict: "incorrect", reason: "Joy ka cycle complete hota hai, sirf permanent loss ki baat nahi." },
      { label: "c", text: "the movement from active life toward rest and old age", verdict: "correct", reason: "Darkening green evening aur life ke later stage ka symbol hai." },
      { label: "d", text: "anger of the old people", verdict: "incorrect", reason: "Old people reflective hain, angry nahi." },
    ],
  },
];

const answerBuilding = [
  "Central idea me childhood joy ke sath life cycle ka point bhi mention karo.",
  "Tone wale answer me cheerful aur nostalgic dono words use karo.",
  "Darkening green ki explanation me evening scene aur symbolic old age dono connect karo.",
  "Children aur old people wale answer me beginning and later life ka contrast useful hota hai.",
];

usePageSeo({
  title: "The Echoing Green Notes | 11th Class English Unit 6",
  description: "11th class English Unit 6 poem notes with summary, central idea, tone, symbolism, literary explanation and MCQ support.",
  keywords: "the echoing green notes, 11th class english unit 6, class 11 poem notes, the echoing green summary, william blake poem notes",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Poems", item: "/11th/english/poems/" },
    { name: "The Echoing Green", item: "/11th/english/poems/the-echoing-green/" },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="app-page-stack">
      <header class="app-page-header">
        <p class="app-page-kicker">Unit 6</p>
        <h1 class="app-page-title">The Echoing Green</h1>
        <p class="app-page-intro">
          Yeh page poem ke childhood joy, old-age reflection aur passage-of-time symbolism ko exam prep format me explain karta hai.
        </p>
      </header>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Textbook-Aligned Snapshot</h2>
        <ul class="app-list">
          <li v-for="point in textbookAlignedPoints" :key="point">{{ point }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Unit 6 Coverage Checklist</h2>
        <ul class="app-list-grid">
          <li v-for="item in chapterCoverage" :key="item" class="english-mini-card-muted">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Quick Revision Points</h2>
        <ul class="app-list">
          <li v-for="item in quickRevisionPoints" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Reading and Explanation Answers</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in readingQuestions"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <p class="app-rich-text mt-3">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Literary Points</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in literaryPoints"
            :key="item.label"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.label }}</h3>
            <p class="app-rich-text mt-3">{{ item.detail }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">MCQs with Option Explanation</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in mcqs"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <ul class="mt-3 space-y-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
              <li v-for="option in item.options" :key="option.label">
                <span class="font-medium text-slate-900">{{ option.label }}. {{ option.text }}</span>
                <span>{{ option.verdict === "correct" ? " Correct:" : " Not correct:" }} {{ option.reason }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Answer-Building Notes</h2>
        <ul class="app-list">
          <li v-for="item in answerBuilding" :key="item">{{ item }}</li>
        </ul>
      </section>
    </section>
  </PageContainer>
</template>





